import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical, AlertTriangle, Save } from 'lucide-react';
import { getChecklistIcon } from '../config/emergencyChecklists';

/**
 * EmergencyChecklistEditModal - Edit emergency checklist procedures
 * 
 * Features:
 * - Add/remove/reorder steps
 * - Mark steps as critical or optional
 * - Preserve regulatory base steps
 * - Track customizations
 */
const EmergencyChecklistEditModal = ({ isOpen, onClose, checklist, onSave }) => {
  const [editedSteps, setEditedSteps] = useState([]);
  const [newStep, setNewStep] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedStep, setDraggedStep] = useState(null);

  // Initialize steps when checklist changes
  useEffect(() => {
    if (checklist && checklist.steps) {
      const steps = checklist.steps.map((step, index) => ({
        id: step.id || `step-${index}`,
        text: typeof step === 'string' ? step : step.text,
        required: typeof step === 'string' ? true : (step.required !== false),
        critical: step.critical || false,
        isCustom: step.isCustom || false,
        originalIndex: index
      }));
      setEditedSteps(steps);
      setHasChanges(false);
    }
  }, [checklist]);

  if (!isOpen || !checklist) return null;

  const Icon = getChecklistIcon(checklist.type);

  // Handle adding new step
  const handleAddStep = () => {
    if (!newStep.trim()) return;
    
    const newStepObj = {
      id: `custom-${Date.now()}`,
      text: newStep.trim(),
      required: true,
      critical: false,
      isCustom: true,
      originalIndex: -1
    };
    
    setEditedSteps([...editedSteps, newStepObj]);
    setNewStep('');
    setHasChanges(true);
  };

  // Handle removing step
  const handleRemoveStep = (stepId) => {
    const step = editedSteps.find(s => s.id === stepId);
    
    // Warn if removing non-custom step
    if (!step.isCustom) {
      const confirmed = window.confirm(
        'This appears to be a standard emergency procedure. Are you sure you want to remove it? This may affect compliance.'
      );
      if (!confirmed) return;
    }
    
    setEditedSteps(editedSteps.filter(s => s.id !== stepId));
    setHasChanges(true);
  };

  // Handle editing step text
  const handleEditStep = (stepId, newText) => {
    setEditedSteps(editedSteps.map(step => 
      step.id === stepId 
        ? { ...step, text: newText }
        : step
    ));
    setHasChanges(true);
  };

  // Toggle critical status
  const toggleCritical = (stepId) => {
    setEditedSteps(editedSteps.map(step => 
      step.id === stepId 
        ? { ...step, critical: !step.critical }
        : step
    ));
    setHasChanges(true);
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedStep(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedStep === null) return;
    
    const draggedItem = editedSteps[draggedStep];
    const newSteps = [...editedSteps];
    
    // Remove from old position
    newSteps.splice(draggedStep, 1);
    
    // Insert at new position
    newSteps.splice(dropIndex, 0, draggedItem);
    
    setEditedSteps(newSteps);
    setDraggedStep(null);
    setHasChanges(true);
  };

  // Save changes
  const handleSave = () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    // Convert back to checklist format
    const updatedChecklist = {
      ...checklist,
      steps: editedSteps.map(step => ({
        id: step.id,
        text: step.text,
        required: step.required,
        critical: step.critical,
        isCustom: step.isCustom
      })),
      lastModified: new Date().toISOString(),
      isCustomized: true
    };

    onSave(updatedChecklist);
    onClose();
  };

  return (
    <div className="emergency-edit-modal-overlay">
      <div className="emergency-edit-modal">
        {/* Header */}
        <div className="modal-header">
          <div className="header-left">
            <Icon size={28} />
            <div>
              <h2>Edit: {checklist.title}</h2>
              <span className="subtitle-text">Customize emergency procedures for your vessel</span>
            </div>
          </div>
          <button onClick={onClose} className="close-button">
            <X size={24} />
          </button>
        </div>

        {/* Warning */}
        <div className="edit-warning">
          <AlertTriangle size={20} />
          <div>
            <strong>Important:</strong> These procedures become part of your vessel's Safety Management System. 
            Ensure all changes comply with regulatory requirements and are tested during drills.
          </div>
        </div>

        {/* Steps Editor */}
        <div className="steps-editor">
          <h3>Emergency Response Steps</h3>
          <div className="steps-list">
            {editedSteps.map((step, index) => (
              <div 
                key={step.id} 
                className={`edit-step ${step.isCustom ? 'custom-step' : ''} ${step.critical ? 'critical-step' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <div className="step-handle">
                  <GripVertical size={16} />
                  <span className="step-number">{index + 1}</span>
                </div>
                
                <div className="step-content">
                  <textarea
                    value={step.text}
                    onChange={(e) => handleEditStep(step.id, e.target.value)}
                    className="step-text-input"
                    rows={2}
                  />
                  <div className="step-controls">
                    <button
                      className={`critical-toggle ${step.critical ? 'active' : ''}`}
                      onClick={() => toggleCritical(step.id)}
                      title="Mark as critical"
                    >
                      {step.critical ? 'CRITICAL' : 'Mark Critical'}
                    </button>
                    {step.isCustom && (
                      <span className="custom-badge">Custom</span>
                    )}
                  </div>
                </div>
                
                <button
                  className="remove-step-btn"
                  onClick={() => handleRemoveStep(step.id)}
                  title="Remove step"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Add New Step */}
        <div className="add-step-section">
          <h3>Add Custom Step</h3>
          <div className="add-step-form">
            <textarea
              value={newStep}
              onChange={(e) => setNewStep(e.target.value)}
              placeholder="Enter new emergency response step..."
              className="new-step-input"
              rows={2}
            />
            <button
              className="btn-add"
              onClick={handleAddStep}
              disabled={!newStep.trim()}
            >
              <Plus size={16} />
              Add Step
            </button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="modal-actions">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className={`btn-primary ${!hasChanges ? 'disabled' : ''}`}
            disabled={!hasChanges}
          >
            <Save size={18} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmergencyChecklistEditModal;